import { useEffect, useState } from 'react';
import { etiquetaIdioma } from '../lib/idioma';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AlarmClock, Archive, CalendarDays, Camera, Check, CheckCircle2, ChevronLeft, CornerUpLeft, FilePlus2, Flame, FolderClock, Globe, Hammer,
  History, Home, MapPin, Maximize2, MoreHorizontal, Paperclip, Pause, Pencil, Plus, Ruler, Send as SendIcon, Users, X,
} from 'lucide-react';
import { uploadFile } from '../lib/upload';
import { api, ApiError } from '../lib/api';
import { approveForRepair } from '../lib/approveRepair';
import { formatUsd, roofingPitch, fmtDateOnly, localeUI } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { CopyBtn } from './ui';
import { DecisionBar } from './ui/DecisionBar';
import { statusColor } from '../lib/status';
import { usePersistentState } from '../lib/usePersistentState';
import { useAuth, hasRole } from '../lib/auth';
import { followUpState, followUpLabel, FOLLOWUP_COLOR } from '../lib/followUpColor';
import { PhoneLink } from './PhoneLink';
import { ContractorLink } from './ContractorCard';
import { InsuranceLink } from './InsuranceCard';
import { AdjusterInfoBox } from './AdjusterInfoBox';
import { AddToViewSelect } from './AddToViewSelect';
import { ClaimHistory } from './ClaimHistory';
import { ClaimVideoButtons } from './ClaimVideoButtons';
import { AssignmentHistoryModal } from './AssignmentHistoryModal';
import { ClaimAlerts } from './ClaimAlerts';
import { ClaimTypeChip } from './ClaimTypeChip';
import { ClaimPaymentsFull } from './ClaimPaymentsFull';
import { ClaimChat } from './ClaimChat';
import { ContractPanel } from './ContractPanel';
import { ClaimFiles } from './ClaimFiles';
import { ContractorFilesPanel } from './ContractorFilesPanel';
import { ClaimEmailBox } from './ClaimEmailBox';
import { InfoPendingBox } from './InfoPendingBox';
import { SupplementsTimeline } from './SupplementsTimeline';
import { SupplementSubmissions } from './SupplementSubmissions';
import { SupplementReview } from './SupplementReview';
import { InspectionTimeline } from './InspectionTimeline';
import { InspectionContactBox } from './InspectionContactBox';
import { ContractorFilesCard } from './ContractorFilesCard';
import { HoCommunicationTab } from './HoCommunicationTab';
import { HoUpdatesTab } from './HoUpdatesTab';
import { LIFECYCLE } from '../lib/lifecycle';
import { WelcomeCallBox } from './WelcomeCallBox';
import { ClaimAppointmentsPanel } from './ClaimAppointmentsPanel';
import { ClaimVideosBox, ClaimVideosChip } from './ClaimVideosBox';

/**
 * Hook reutilizable: mantiene el claim seleccionado y renderiza el PANEL lateral estilo
 * Monday (overlay fijo a la derecha con pestañas). Uso: const cd = useClaimDrawer();
 * ... {cd.drawer}; y en el nombre del cliente <ClientNameButton onOpen={cd.open} .../>.
 */
export function useClaimDrawer(opts?: { float?: boolean; showChat?: boolean }) {
  // Persistido por ruta → al volver del claim completo (botón atrás) reabre el mismo cliente.
  const { pathname } = useLocation();
  const [sel, setSel] = usePersistentState<string | null>(`drawer:${pathname}`, null);
  // Pestaña inicial opcional (ej. el To Do abre directo el chat del CONTRACTOR). No persiste.
  const [initialTab, setInitialTab] = useState<string | null>(null);
  void opts; // el panel ahora SIEMPRE es overlay fijo (estilo Monday); float/showChat quedan por compat.
  const drawer = sel ? <ClaimDrawer claimId={sel} initialTab={initialTab ?? undefined} onClose={() => { setSel(null); setInitialTab(null); }} /> : null;
  return {
    sel,
    open: (id: string, tab?: string) => { setInitialTab(tab ?? null); setSel(id); },
    close: () => { setSel(null); setInitialTab(null); },
    drawer,
  };
}

/** Nombre del cliente clickeable que abre la tarjeta lateral (no navega). */
export function ClientNameButton({ claimId, name, onOpen, bold = true }: { claimId: string; name: React.ReactNode; onOpen: (id: string) => void; bold?: boolean }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(claimId); }}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', font: 'inherit', fontWeight: bold ? 'var(--fw-semibold)' : 'var(--fw-body)', textAlign: 'left' }}>
      {name}
    </button>
  );
}

export type ClaimSummary = {
  /** File a new claim: claim anterior / claim nuevo (chips del encabezado). */
  previous_claim?: { id: string; claim_number: string | null } | null;
  next_claim?: { id: string; claim_number: string | null } | null;
  claim_number: string | null;
  policy_number?: string | null;
  homeowner_name: string | null;
  /** Etiqueta 2 del claim + de dónde salió el chip que se ve (ver `lib/claimType`). */
  claim_type?: string | null;
  type_of_loss?: string | null;
  claim_type_by?: string | null;
  claim_type_at?: string | null;
  homeowner_email: string | null;
  phone: string | null;
  address: string | null;
  contractor_name: string | null;
  contractor_id: string | null;
  date_of_loss: string | null;
  insurance_company: string | null;
  /** La cita (próxima futura o la última pasada) para el encabezado; null si nunca hubo. */
  next_inspection?: { scheduled_start: string; type: string; who: string | null; done: boolean; upcoming: boolean } | null;
  rcv_amount_cents: number | null;
  deductible_cents: number | null;
  acv_cents: number | null;
  current_status: string | null;   // status de PAGOS (board de Payments)
  lifecycle_status?: string | null; // status del CLAIM (las 26 etapas)
  owner_dept: string | null; // depto dueño de la etapa del claim → default del chat del drawer
  companycam_url: string | null;
  adjuster_info: string | null;
  property_id: string | null;
  roof_pitch_deg: number | null;
  roof_squares: number | null;
  street_view_url: string | null;
  preferred_language?: string | null;
  is_existing_claim?: boolean;
  archived_at?: string | null;
  payment_hold?: boolean;
  payment_hold_note?: string | null;
  last_follow_up: { created_at: string; note: string | null; stage: string | null } | null;
};

type DeptPresence = {
  department_key?: string; department_name: string; status: string | null;
  assigned_team_name: string | null; last_follow_up_at: string | null; next_follow_up_at: string | null;
};

/** Botón de copiar al portapapeles con feedback ✓ (1.2s). */

/** Campo editable inline: al ✎ muestra input y guarda vía PUT /api/claims/:id/info (que LOGUEA
 *  el cambio old→new en el Activity Log — así se conserva el historial como el adjuster info). */
function InlineField({ claimId, field, initial, money, date, placeholder, onSaved, children }: {
  claimId: string; field: string; initial: string; money?: boolean; date?: boolean; placeholder?: string; onSaved?: () => void; children: React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(initial ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setV(initial ?? ''); }, [initial, editing]);
  async function save() {
    setBusy(true);
    const payload: Record<string, string | null> = {};
    if (money) { const d = v.replace(/[^\d.]/g, ''); const cents = d ? Math.round(parseFloat(d) * 100) : null; payload[field] = cents == null || !Number.isFinite(cents) ? null : String(cents); }
    else payload[field] = v.trim() || null;
    try { await api.put(`/api/claims/${claimId}/info`, payload); setEditing(false); onSaved?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        {children}
        <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }} title="Editar" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: 0.4, fontSize: 'var(--fs-2xs)', lineHeight: 1 }}><Pencil size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
      <input autoFocus className="input sm" style={{ minWidth: 120 }} type={date ? 'date' : 'text'} value={v} placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setV(initial ?? ''); } }} />
      <button className="btn sm" disabled={busy} onClick={save} title="Guardar"><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
      <button className="btn ghost sm" onClick={() => { setEditing(false); setV(initial ?? ''); }} title="Cancelar"><X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
    </span>
  );
}

function Row({ label, value, copy, href }: { label: string; value: React.ReactNode; copy?: string | null; href?: string | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--line)', alignItems: 'center' }}>
      <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{label}</span>
      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', textAlign: 'right', display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        {href && value ? (
          <a href={href} style={{ color: 'var(--link)', textDecoration: 'none', wordBreak: 'break-word' }} onClick={(e) => e.stopPropagation()}>{value}</a>
        ) : (
          <span style={{ wordBreak: 'break-word' }}>{value ?? '—'}</span>
        )}
        {copy && <CopyBtn value={copy} title={label} />}
      </span>
    </div>
  );
}

/** Título de sección dentro de una pestaña. */
function SecTitle({ children }: { children: React.ReactNode }) {
  return <div className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{children}</div>;
}

/* ---------- Pestaña ITEM CARD: fotos + dinero + toda la ficha ---------- */
function ItemCardTab({ claimId, s, onSaved }: { claimId: string; s: ClaimSummary | null; onSaved?: () => void }) {
  const { t } = useI18n();
  const [money, setMoney] = useState<{ expectedCents: number; receivedCents: number } | null>(null);
  const [intel, setIntel] = useState<{ satellite_url: string | null; street_view_url: string | null; roof_squares: number | null; roof_pitch_deg: number | null } | null>(null);
  useEffect(() => {
    let alive = true;
    api.get<{ expectedCents: number; receivedCents: number }>(`/api/claims/${claimId}/money`).then((r) => { if (alive) setMoney(r); }).catch(() => {});
    api.get<{ satellite_url: string | null; street_view_url: string | null; roof_squares: number | null; roof_pitch_deg: number | null }>(`/api/claims/${claimId}/property-intel`).then((r) => { if (alive) setIntel(r); }).catch(() => {});
    return () => { alive = false; };
  }, [claimId]);
  const pitch = roofingPitch(intel?.roof_pitch_deg ?? s?.roof_pitch_deg ?? null);
  // RCV inicial (primer estimado del seguro) vs FINAL (esperado hoy con suplementos).
  const rcvInitial = s?.rcv_amount_cents ?? null;
  const rcvFinal = money && money.expectedCents > 0 ? money.expectedCents : rcvInitial;
  const suppDelta = rcvInitial != null && rcvFinal != null ? rcvFinal - rcvInitial : null;
  const pct = money && money.expectedCents > 0 ? Math.min(100, Math.round((money.receivedCents / money.expectedCents) * 100)) : null;
  const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'], letterSpacing: -0.5 };
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* FOTOS DOBLES: casa (street view) + techo (satélite) con roof squares + pitch. */}
      {(intel?.satellite_url || intel?.street_view_url) && (
        <div>
          <SecTitle><Home size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdw.houseRoof')}</SecTitle>
          <div style={{ display: 'flex', gap: 10 }}>
            {intel?.street_view_url && <img src={intel.street_view_url} alt="house" style={{ flex: 1, minWidth: 0, height: 170, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--line)' }} />}
            {intel?.satellite_url && (
              <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <img src={intel.satellite_url} alt="roof" style={{ width: '100%', height: 170, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--line)' }} />
                {(intel.roof_squares != null || pitch) && (
                  <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 4 }}>
                    {/* Overlays sobre la foto: fondo negro translúcido + texto blanco A PROPÓSITO (legibilidad sobre imagen, igual en dark). */}
                    {intel.roof_squares != null && <span style={{ background: 'rgba(0,0,0,0.72)', color: '#fff', fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', padding: '2px 7px', borderRadius: 6 }}><Ruler size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {intel.roof_squares} sq</span>}
                    {pitch && <span style={{ background: 'rgba(0,0,0,0.72)', color: '#fff', fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', padding: '2px 7px', borderRadius: 6 }}>▲ {pitch}</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* DINERO: RCV inicial / final / incremento por suplementos + barra de pago. */}
      <div className="card" style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <div>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cdw.rcvInitial')}</div>
            <div style={{ ...mono, fontSize: 'var(--fs-lg)' }}><InlineField claimId={claimId} field="rcv_amount_cents" money initial={rcvInitial != null && rcvInitial > 0 ? String(rcvInitial / 100) : ''} placeholder="$" onSaved={onSaved}>{rcvInitial != null ? formatUsd(rcvInitial) : '—'}</InlineField></div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cdw.rcvFinal')}</div>
            <div style={{ ...mono, fontSize: 'var(--fs-lg)' }}>{rcvFinal != null ? formatUsd(rcvFinal) : '—'}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cdw.suppIncrease')}</div>
            <div style={{ ...mono, fontSize: 'var(--fs-lg)', color: suppDelta != null && suppDelta > 0 ? 'var(--green)' : undefined }}>
              {suppDelta != null && suppDelta !== 0 ? `${suppDelta > 0 ? '+' : ''}${formatUsd(suppDelta)}` : '—'}
            </div>
          </div>
        </div>
        {pct != null && money && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-2xs)', marginBottom: 5, flexWrap: 'wrap', gap: 4 }}>
              <span className="muted">{t('cd.received')}: <strong style={{ color: 'var(--ink)', fontFamily: 'var(--font-mono)' }}>{formatUsd(money.receivedCents)}</strong></span>
              <span className="muted">{t('cd.pending')}: {formatUsd(Math.max(0, money.expectedCents - money.receivedCents))} ({pct}%)</span>
            </div>
            <div style={{ height: 10, borderRadius: 999, background: 'var(--surface-2)', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? 'var(--green)' : 'var(--accent)', transition: 'width .3s' }} />
            </div>
          </div>
        )}
      </div>

      {/* FICHA (dos columnas). El header ya trae nombre/dirección/seguro/claim# — aquí NO se repiten. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '4px 24px' }}>
        <div>
          <div className="muted" style={{ marginTop: 6, marginBottom: 2, fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cdw.client')}</div>
          {/* Campos editables inline (✎) → PUT /info logea old→new en el Activity Log (historial). */}
          <Row label="Email" value={<InlineField claimId={claimId} field="homeowner_email" initial={s?.homeowner_email ?? ''} placeholder="email@…" onSaved={onSaved}>{s?.homeowner_email ? <a href={`mailto:${s.homeowner_email}`} style={{ color: 'var(--link)' }} onClick={(e) => e.stopPropagation()}>{s.homeowner_email}</a> : '—'}</InlineField>} copy={s?.homeowner_email} />
          <Row label="Tel" value={<InlineField claimId={claimId} field="phone" initial={s?.phone ?? ''} placeholder="tel" onSaved={onSaved}>{s?.phone ? <PhoneLink phone={s.phone} claimId={claimId} recipientType="homeowner" style={{ color: 'var(--link)' }} /> : '—'}</InlineField>} copy={s?.phone} />
          <Row label={t('cdw.contractor')} value={<ContractorLink name={s?.contractor_name} contractorId={s?.contractor_id} />} />
          <Row label={t('cdw.dateOfLoss')} value={<InlineField claimId={claimId} field="date_of_loss" date initial={s?.date_of_loss ?? ''} onSaved={onSaved}>{fmtDateOnly(s?.date_of_loss ?? null)}</InlineField>} />
        </div>
        <div>
          <div className="muted" style={{ marginTop: 6, marginBottom: 2, fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('q.insurance')}</div>
          <Row label={t('cdw.policyNumber')} value={<InlineField claimId={claimId} field="policy_number" initial={s?.policy_number ?? ''} placeholder="policy #" onSaved={onSaved}>{s?.policy_number || '—'}</InlineField>} copy={s?.policy_number} />
          <Row label={t('cdw.deductible')} value={<InlineField claimId={claimId} field="deductible_cents" money initial={s?.deductible_cents != null && s.deductible_cents > 0 ? String(s.deductible_cents / 100) : ''} placeholder="$" onSaved={onSaved}>{s?.deductible_cents != null ? formatUsd(s.deductible_cents) : '—'}</InlineField>} />
          <Row label={t('cdw.pitch')} value={pitch} />
          <Row label={t('cdw.squares')} value={(intel?.roof_squares ?? s?.roof_squares) != null ? String(intel?.roof_squares ?? s?.roof_squares) : null} />
        </div>
      </div>
      <AdjusterInfoBox claimId={claimId} initial={s?.adjuster_info} compact />
    </div>
  );
}

/* ---------- Pestaña TASKS: historial vivo con barra de color + animación ---------- */
type DrawerTask = { id: string; title: string; note: string | null; due_date: string | null; priority: string; status: string; assigned_by: string | null; assigned_to: string | null; team_name?: string | null; department_name?: string | null; completed_by?: string | null; created_at?: string | null };
type Target = { id: string; name: string };

function TasksTab({ claimId, presetTeam }: { claimId: string; presetTeam: string | null }) {
  const { t } = useI18n();
  const [tasks, setTasks] = useState<DrawerTask[]>([]);
  const [users, setUsers] = useState<Target[]>([]);
  const [teams, setTeams] = useState<Target[]>([]);
  const [departments, setDepartments] = useState<Target[]>([]);
  const [assignee, setAssignee] = useState('');
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api.get<{ tasks: DrawerTask[] }>(`/api/claims/${claimId}/tasks`).then((r) => setTasks(r.tasks)).catch(() => {});
  useEffect(() => {
    load();
    api.get<{ users: Target[]; teams: Target[]; departments?: Target[] }>('/api/team/mention-targets')
      .then((r) => { setUsers(r.users || []); setTeams(r.teams || []); setDepartments(r.departments || []); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);
  // Team preseleccionado (llegó del chip 👥 del header): matchear por nombre cuando carguen los teams.
  useEffect(() => {
    if (!presetTeam || !teams.length) return;
    const tm = teams.find((x) => x.name.trim().toLowerCase() === presetTeam.trim().toLowerCase());
    if (tm) setAssignee(`team:${tm.id}`);
  }, [presetTeam, teams]);

  async function assign() {
    if (!assignee || !title.trim()) return;
    const [kind, id] = assignee.split(':');
    setBusy(true);
    try {
      await api.post('/api/tasks', {
        claimId, title: title.trim(), dueDate: due || undefined,
        assignedToUserId: kind === 'user' ? id : undefined,
        assignedToTeamId: kind === 'team' ? id : undefined,
        assignedToDepartmentId: kind === 'dept' ? id : undefined,
      });
      setTitle(''); setDue(''); await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function markDone(id: string) {
    try { await api.post(`/api/tasks/${id}/done`, {}); await load(); } catch { /* */ }
  }

  const openTasks = tasks.filter((tk) => tk.status !== 'done');
  const doneTasks = tasks.filter((tk) => tk.status === 'done');
  const who = (tk: DrawerTask) => (tk.department_name ? tk.department_name : tk.team_name ? tk.team_name : (tk.assigned_to || '—'));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Composer: asignar una task a un usuario/team/depto (el chip 👥 del header preselecciona). */}
      <div className="card" style={{ display: 'grid', gap: 6 }}>
        <SecTitle><Plus size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('task.assign')}</SecTitle>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <select className="input sm" style={{ minWidth: 180 }} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">{t('task.pickAssignee')}</option>
            {departments.length > 0 && <optgroup label={t('cd.departments')}>{departments.map((d) => <option key={`d${d.id}`} value={`dept:${d.id}`}>{d.name}</option>)}</optgroup>}
            {teams.length > 0 && <optgroup label={t('task.teams')}>{teams.map((tm) => <option key={`t${tm.id}`} value={`team:${tm.id}`}>{tm.name}</option>)}</optgroup>}
            {users.length > 0 && <optgroup label={t('plat.colUsers')}>{users.map((u) => <option key={`u${u.id}`} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>}
          </select>
          <input className="input sm" style={{ flex: 1, minWidth: 200 }} placeholder={t('task.whatToDo')} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') assign(); }} />
          <input className="input sm" type="date" style={{ width: 150 }} value={due} onChange={(e) => setDue(e.target.value)} />
          <button className="btn sm" disabled={busy || !assignee || !title.trim()} onClick={assign}>{t('task.assign')}</button>
        </div>
      </div>

      {/* Abiertas: barra EMBER animada. */}
      <div>
        <SecTitle><Flame size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdw.tasksOpen')} {openTasks.length > 0 && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)' }}>{openTasks.length}</span>}</SecTitle>
        {openTasks.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('task.none')}</div> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {openTasks.map((tk, i) => (
              <div key={tk.id} className="task-row" style={{ animationDelay: `${Math.min(i, 8) * 45}ms`, display: 'flex', overflow: 'hidden', borderRadius: 12, border: '1px solid var(--line)', background: 'var(--card)', boxShadow: 'var(--shadow)' }}>
                <div style={{ width: 6, flexShrink: 0, background: 'var(--accent)' }} />
                <div style={{ flex: 1, minWidth: 0, padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{tk.title}{tk.note ? <span className="muted" style={{ fontWeight: 'var(--fw-body)' }}> — {tk.note}</span> : ''}</div>
                    <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>
                      {who(tk)}{tk.assigned_by ? ` · ${t('cdw.taskBy')} ${tk.assigned_by}` : ''}{tk.due_date ? ` · ${new Date(tk.due_date).toLocaleDateString(localeUI())}` : ''}
                    </div>
                  </div>
                  <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', flexShrink: 0 }} onClick={() => markDone(tk.id)}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('task.done')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Historial (hechas): barra VERDE, se quedan aquí. */}
      <div>
        <SecTitle><CheckCircle2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdw.tasksDone')} {doneTasks.length > 0 && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)' }}>{doneTasks.length}</span>}</SecTitle>
        {doneTasks.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>—</div> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {doneTasks.map((tk, i) => (
              <div key={tk.id} className="task-row" style={{ animationDelay: `${Math.min(i, 8) * 45}ms`, display: 'flex', overflow: 'hidden', borderRadius: 12, border: '1px solid var(--line)', background: 'var(--card)', opacity: 0.85 }}>
                <div style={{ width: 6, flexShrink: 0, background: 'var(--green)' }} />
                <div style={{ flex: 1, minWidth: 0, padding: '9px 12px' }}>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', textDecoration: 'line-through' }}>{tk.title}</div>
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>
                    {who(tk)}{tk.completed_by ? ` · ${t('task.doneBy')} ${tk.completed_by}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- El PANEL (estilo Monday): overlay ancho + acciones fijas + pestañas ---------- */
type TabKey = 'chat' | 'historia' | 'item' | 'files' | 'intake' | 'scheduling' | 'claims' | 'payments' | 'emails' | 'tasks' | 'info-pending' | 'supplements' | 'contractor' | 'ho-comm' | 'ho-updates';

export function ClaimDrawer({ claimId, onClose, showChat, initialTab }: { claimId: string; onClose: () => void; showChat?: boolean; initialTab?: string }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const { me } = useAuth();
  void showChat; // compat con llamadas viejas
  const [s, setS] = useState<ClaimSummary | null>(null);
  const [depts, setDepts] = useState<DeptPresence[]>([]);
  const [loading, setLoading] = useState(true);
  // Abre en General chat (pedido) — salvo que quien abre pida una pestaña (ej. el To Do
  // abre directo el chat del CONTRACTOR cuando hay mensajes del CO por responder).
  const [tab, setTab] = useState<TabKey>((initialTab as TabKey) || 'chat');
  useEffect(() => { if (initialTab) setTab(initialTab as TabKey); }, [initialTab, claimId]);
  // MÓVIL: con el drawer a pantalla completa, el scroll del dedo se iba al BODY de atrás y el
  // drawer parecía congelado ("no se mueve", reporte del dueño 2026-08-08). Mientras está
  // abierto se bloquea el scroll del fondo; solo ≤900px para no cambiar el comportamiento desktop.
  useEffect(() => {
    if (!window.matchMedia('(max-width: 900px)').matches) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  // CABEZAL QUE SE ENCOGE (pedido del dueño 2026-08-08): al scrollear, el bloque grande
  // (nombre/chips/dirección/statuses) se colapsa y arriba queda SOLO nombre · contratista,
  // para darle la pantalla a las pestañas. Solo tiene efecto visual en ≤900px (CSS).
  const [compact, setCompact] = useState(false);
  // Composer rápido del mockup: "Escribe un update…" fijo abajo (posta al General chat),
  // con clip para adjuntar (los archivos viajan con el update al enviar).
  // Modals móviles: Decision y Add to view (centrados, no a pantalla completa).
  const [mDecide, setMDecide] = useState(false);
  const [mViewPick, setMViewPick] = useState(false);
  // Historial de asignaciones (AM + teams): modal que abre el botoncito junto a los dept cards.
  const [showAsgHist, setShowAsgHist] = useState(false);
  const [quick, setQuick] = useState('');
  const [quickFiles, setQuickFiles] = useState<File[]>([]);
  const [quickBusy, setQuickBusy] = useState(false);
  const [chatBump, setChatBump] = useState(0);
  async function sendQuick() {
    const text = quick.trim();
    if ((!text && quickFiles.length === 0) || quickBusy) return;
    setQuickBusy(true);
    try {
      const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const attachments = await Promise.all(quickFiles.map(async (f) => ({ key: await uploadFile(f, 'misc'), name: f.name })));
      // Mismo endpoint del General chat sin board elegido (department null = canal general).
      await api.post(`/api/claims/${claimId}/chat`, { department: null, body: text ? `<p>${esc}</p>` : '', attachments, followUp: false });
      setQuick('');
      setQuickFiles([]);
      setChatBump((n) => n + 1); // remonta el ClaimChat del tab → el update aparece al instante
    } finally {
      setQuickBusy(false);
    }
  }
  const [taskPresetTeam, setTaskPresetTeam] = useState<string | null>(null);
  const [reqSent, setReqSent] = useState<string | null>(null);
  // MÓVIL: menú "⋯" del appbar (View photos / Open claim / Archive) — mismos handlers que
  // los botones de .cdw-actions, que en ≤900px quedan ocultos.
  const [mnavMenu, setMnavMenu] = useState(false);
  // Opciones del selector de PAYMENT STATUS del header (mismas colas de Payments del hero).
  const [payStatuses, setPayStatuses] = useState<Array<{ key: string; label: string }>>([]);
  useEffect(() => {
    let alive = true;
    api.get<{ statuses: Array<{ key: string; label: string }> }>('/api/departments/payments/statuses')
      .then((r) => { if (alive) setPayStatuses(r.statuses); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const isSuper = hasRole(me, 'super-manager');
  const reloadSummary = () => api.get<ClaimSummary>(`/api/claims/${claimId}/summary`).then(setS).catch(() => {});
  const reloadDepts = () => api.get<{ departments: DeptPresence[] }>(`/api/claims/${claimId}/departments`).then((r) => setDepts(r.departments)).catch(() => {});
  // Asignar/quitar EQUIPO desde los cuadros de departamento (feedback 2026-07-29: "en
  // supplements quiero agregar un team desde ahí y no puedo"). office+ como el endpoint.
  const [teamPickFor, setTeamPickFor] = useState<string | null>(null);
  const [teamEditBusy, setTeamEditBusy] = useState<string | null>(null);
  const [teamOptions, setTeamOptions] = useState<Record<string, Array<{ id: string; name: string }>>>({});
  async function loadTeamOptions(deptKey: string) {
    if (teamOptions[deptKey]) return;
    try {
      const r = await api.get<{ teams: Array<{ id: string; name: string }> }>(`/api/teams/options?department=${encodeURIComponent(deptKey)}`);
      setTeamOptions((o) => ({ ...o, [deptKey]: r.teams || [] }));
    } catch { setTeamOptions((o) => ({ ...o, [deptKey]: [] })); }
  }
  async function assignDeptTeam(deptKey: string, teamId: string) {
    setTeamEditBusy(deptKey);
    try { await api.post(`/api/claims/${claimId}/departments/${deptKey}/assigned-team`, { teamId }); await reloadDepts(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setTeamEditBusy(null); setTeamPickFor(null); }
  }
  async function removeDeptTeam(deptKey: string, teamName: string) {
    if (!window.confirm(t('cdw.teamRemoveConfirm', { team: teamName }))) return;
    setTeamEditBusy(deptKey);
    try { await api.post(`/api/claims/${claimId}/departments/${deptKey}/assigned-team`, { teamId: null }); await reloadDepts(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setTeamEditBusy(null); }
  }
  useEffect(() => {
    let alive = true;
    // Al cambiar de claim se resetea a General chat — SALVO que quien abrió pidiera una
    // pestaña (ej. el To Do abre directo el chat del Contractor).
    setLoading(true); setS(null); setDepts([]); setTab((initialTab as TabKey) || 'chat'); setTaskPresetTeam(null); setReqSent(null);
    api.get<ClaimSummary>(`/api/claims/${claimId}/summary`)
      .then((r) => { if (alive) setS(r); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    api.get<{ departments: DeptPresence[] }>(`/api/claims/${claimId}/departments`).then((r) => { if (alive) setDepts(r.departments); }).catch(() => {});
    return () => { alive = false; };
  }, [claimId]);

  /* --- Acciones FIJAS (visibles desde cualquier pestaña) — mismos endpoints que el claim completo. --- */
  async function changeStage(v: string) {
    try { await api.post(`/api/claims/${claimId}/lifecycle-status`, { status: v }); await reloadSummary(); await reloadDepts(); }
    catch (e) { alert(e instanceof ApiError ? e.message : t('hero.reqError')); }
  }
  /** La ▾ de los badges del header: claim status por el lifecycle (routing + evento) y
   *  payment status por current_status (Monday + timeline + evento). office+; con rastro. */
  async function changeStatusBadge(kind: 'claim' | 'payment', v: string) {
    if (kind === 'claim') { await changeStage(v); return; }
    try {
      await api.post(`/api/claims/${claimId}/payment-status`, { status: v });
      await reloadSummary(); await reloadDepts();
    } catch (e) { alert(e instanceof ApiError ? e.message : t('hero.reqError')); }
  }
  async function approveInsurance() {
    try { await api.post(`/api/claims/${claimId}/approve-insurance`, {}); await reloadSummary(); await reloadDepts(); }
    catch (e) { alert(e instanceof ApiError ? e.message : t('hero.reqError')); }
  }
  /** Approved for repair: lifecycle + clasificación de pago (pregunta si el claim no tiene números). */
  async function approveRepairBtn() {
    try {
      await api.post(`/api/claims/${claimId}/lifecycle-status`, { status: 'Approved for Repairs' });
      await approveForRepair(claimId, t('afr.ask'));
      await reloadSummary(); await reloadDepts();
    } catch (e) { alert(e instanceof ApiError ? e.message : t('hero.reqError')); }
  }
  async function requestApproval(kind: 'hold' | 'cancel_claim' | 'release_letter', promptLabel: string) {
    const reason = window.prompt(promptLabel);
    if (reason === null) return;
    if (kind === 'release_letter' && !reason.trim()) { alert(t('rl.reasonRequired')); return; }
    try { await api.post(`/api/claims/${claimId}/requests`, { kind, reason: reason.trim() || null }); setReqSent(kind); }
    catch { alert(t('hero.reqError')); }
  }
  async function archiveAction() {
    const archived = !!s?.archived_at;
    if (archived) {
      if (!window.confirm(t('archive.unarchiveConfirm'))) return;
      try { await api.post(`/api/claims/${claimId}/archive`, { archived: false }); await reloadSummary(); } catch { /* */ }
    } else if (isSuper) {
      if (!window.confirm(t('archive.confirm'))) return;
      try { await api.post(`/api/claims/${claimId}/archive`, { archived: true }); await reloadSummary(); } catch { /* */ }
    } else {
      // Prompt con MOTIVO (feedback 2026-07-29): el confirm no dejaba escribir la razón.
      const reason = window.prompt(t('archive.reasonPrompt'));
      if (reason === null) return;
      try { await api.post(`/api/claims/${claimId}/requests`, { kind: 'archive', reason: reason.trim() || null }); setReqSent('archive'); } catch { /* */ }
    }
  }

  const TABS: Array<{ key: TabKey; label: string }> = [
    { key: 'chat', label: t('cdw.tabChat') },
    { key: 'item', label: t('cdw.tabItem') },
    { key: 'files', label: t('cdw.tabFiles') },
    { key: 'intake', label: t('cdw.tabIntake') },
    { key: 'scheduling', label: t('cdw.tabScheduling') },
    { key: 'claims', label: t('cdw.tabClaims') }, // los updates del depto de Claims (pedido 2026-10-08; por ahora solo el chat)
    { key: 'payments', label: t('cdw.tabPayments') },
    // La historia va PRIMERA después del chat: es la pregunta más frecuente y hasta ahora
    // obligaba a recorrer las otras pestañas para reconstruirla a mano.
    { key: 'historia', label: t('cdw.tabHistory') },
    { key: 'supplements', label: t('cdw.tabSupplements') }, // timeline del estimado/Supplements
    { key: 'contractor', label: t('cd.tabContractor') }, // chat con el CO + sus files
    // Todo lo hablado con el HOMEOWNER (llamadas, SMS y correos) en una sola línea de tiempo.
    { key: 'ho-comm', label: t('hoc.tab') },
    // El update semanal al homeowner: la semana en vivo, los borradores y el rastreo.
    { key: 'ho-updates', label: t('hou.tab') },
    { key: 'emails', label: t('cdw.tabEmails') },
    { key: 'tasks', label: t('cdw.tabTasks') }, // de ÚLTIMA (pedido)
    { key: 'info-pending', label: t('cd.tabInfoPending') }, // Info Pending junto a Tasks (pedido)
  ];
  // Botones de decisión COMPARTIDOS entre la barra de desktop y el modal centrado de móvil
  // (mismos handlers/permisos; definirlos una sola vez evita que se desincronicen).
  const decisionButtons = (
    <>
      <button className="btn sm solid-good" onClick={approveInsurance}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.approved')}</button>
      <button className="btn sm solid-warn" onClick={approveRepairBtn}><Hammer size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.approvedRepair')}</button>
      <button className="btn sm solid-bad" onClick={() => changeStage('Denied')}><X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.denied')}</button>
      {reqSent === 'hold'
        ? <span className="badge orange" style={{ fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.reqSent')}</span>
        : <button className="btn ghost sm" style={{ fontWeight: 'var(--fw-bold)' }} onClick={() => requestApproval('hold', t('hero.holdReason'))}><Pause size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.hold')}</button>}
      {reqSent === 'cancel_claim'
        ? <span className="badge orange" style={{ fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.reqSent')}</span>
        : <button className="btn ghost sm" style={{ fontWeight: 'var(--fw-bold)' }} onClick={() => requestApproval('cancel_claim', t('hero.cancelReason'))}><X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.cancel')}</button>}
      {reqSent === 'release_letter'
        ? <span className="badge orange" style={{ fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hero.reqSent')}</span>
        : <button className="btn ghost sm" style={{ fontWeight: 'var(--fw-bold)' }} onClick={() => requestApproval('release_letter', t('rl.reasonPrompt'))} title={t('rl.hint')}><Paperclip size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rl.request')}</button>}
      {reqSent === 'archive'
        ? <span className="badge orange" style={{ fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('archive.requested')}</span>
        : <button className="btn ghost sm" style={{ fontWeight: 'var(--fw-bold)' }} onClick={archiveAction}>{s?.archived_at
            ? <><CornerUpLeft size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('archive.unarchive')}</>
            : <><Archive size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {isSuper ? t('archive.archive') : t('archive.request')}</>}</button>}
    </>
  );

  return (
    <div
      className={`claim-drawer${compact ? ' compact' : ''}`}
      style={{
        // Overlay estilo Monday: fijo a la derecha, ancho hasta ~la columna del cliente.
        // En móvil (≤900px) la clase .claim-drawer lo abre a PANTALLA COMPLETA (Fase 5).
        position: 'fixed', top: 66, right: 12, bottom: 12, zIndex: 60,
        width: 'min(980px, calc(100vw - 460px))', minWidth: 540,
        background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius)',
        boxShadow: '0 16px 48px rgba(12,18,29,0.22)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
    >
      {/* CABEZAL FIJO: nombre + chip new/existing + status + idioma; dirección; seguro·claim#.
          Queda SIEMPRE arriba (no scrollea) — lo demás va en el scroll único de abajo. */}
      <div className="cdw-head" style={{ padding: '16px 18px 10px', flexShrink: 0 }}>
        {/* APPBAR MÓVIL (mockup B-mobile "2 · El claim"): ‹ Claims (volver) + chip NEW/EXISTING
            con el claim# + menú "⋯" con las MISMAS acciones de .cdw-actions (que en móvil se
            oculta). Solo existe visualmente en ≤900px (.cdw-mnav arranca display:none). */}
        <div className="cdw-mnav">
          <button type="button" className="cdw-mback" onClick={onClose}>
            <ChevronLeft size={18} strokeWidth={2.25} /> {t('nav.claims')}
          </button>
          {s && (
            <span className={`cdw-mref${s.is_existing_claim ? ' is-ex' : ''}`}>
              <span className="cdw-mref-tag">{s.is_existing_claim ? t('cdw.existingClaim') : t('cdw.newClaim')}</span>
              {s.claim_number || `#${claimId}`}
            </span>
          )}
          {/* Al encogerse el cabezal (scroll), el chip del ref cede su lugar a nombre · contratista. */}
          <span className="cdw-mtitle">
            {s?.homeowner_name || ''}{s?.contractor_name ? ` · ${s.contractor_name}` : ''}
          </span>
          <span className="cdw-mmore">
            <button type="button" className="btn ghost sm" onClick={() => setMnavMenu((v) => !v)} title="More">
              <MoreHorizontal size={16} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
            </button>
            {mnavMenu && (
              <>
                {/* Backdrop invisible: tocar afuera cierra el menú. */}
                <div className="cdw-mmenu-backdrop" onClick={() => setMnavMenu(false)} />
                <div className="cdw-mmenu">
                  {s?.companycam_url && (
                    <a href={s.companycam_url} target="_blank" rel="noopener noreferrer" onClick={() => setMnavMenu(false)}>
                      <Camera size={14} strokeWidth={1.75} /> {t('cdw.viewPhotos')}
                    </a>
                  )}
                  <button type="button" onClick={() => { setMnavMenu(false); nav(`/claims/${claimId}`); }}>
                    <Maximize2 size={14} strokeWidth={1.75} /> {t('cdw.openClaim')}
                  </button>
                  <button type="button" onClick={() => { setMnavMenu(false); void archiveAction(); }}>
                    {s?.archived_at
                      ? <><CornerUpLeft size={14} strokeWidth={1.75} /> {t('archive.unarchive')}</>
                      : <><Archive size={14} strokeWidth={1.75} /> {isSuper ? t('archive.archive') : t('archive.request')}</>}
                  </button>
                </div>
              </>
            )}
          </span>
        </div>
        <div className="cdw-headrow" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div className="cdw-headmain" style={{ minWidth: 0 }}>
            <div className="cdw-namerow" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 className="cdw-name" style={{ margin: 0, fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-bold)', lineHeight: 1.1, letterSpacing: -0.4, wordBreak: 'break-word' }}>
                {s?.homeowner_name || (loading ? '…' : t('claims.statusNew'))}
              </h2>
              {s?.homeowner_name && <span className="cdw-namecopy"><CopyBtn value={s.homeowner_name} title={t('claims.homeowner')} /></span>}
              {/* QUÉ TIPO DE CLAIM ES, pegado al nombre (pedido del dueño 2026-08-27). Nunca en
                  blanco: si nadie lo eligió se deduce del nombre / del type_of_loss, y si no,
                  Roof. Se cambia con un clic y queda el rastro de quién fue. */}
              {s && <ClaimTypeChip claimId={claimId} claim={s} onSaved={reloadSummary} />}
              {/* NEW vs EXISTING con color que identifica la tarjeta + el CLAIM # GRANDE debajo,
                  en la misma etiqueta y color (pedido: el claim number no se veía). */}
              {s && (() => {
                const isEx = s.is_existing_claim;
                const bg = isEx ? 'var(--accent-soft)' : 'var(--green-soft)';
                const fg = isEx ? 'var(--accent)' : 'var(--green)';
                return (
                  <span className="cdw-refchip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', background: bg, color: fg, borderRadius: 9, padding: '3px 11px', lineHeight: 1.12 }}>
                      <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', letterSpacing: 0.5, textTransform: 'uppercase' }}>{isEx ? t('cdw.existingClaim') : t('cdw.newClaim')}</span>
                      <span style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font-mono)', letterSpacing: -0.3 }}>{s.claim_number || `#${claimId}`}</span>
                    </span>
                    <CopyBtn value={s.claim_number} title={t('claims.num')} />
                  </span>
                );
              })()}
              {/* Los DOS status, siempre visibles y rotulados, UNO DEBAJO DEL OTRO y grandes
                  (pedido del dueño): claim status arriba, payment status abajo. La flechita ▾
                  cambia el status ahí mismo — cualquier usuario (office+), SIEMPRE con rastro
                  (lifecycle-status y department status registran evento + quién). */}
              <span className="cdw-statuscol" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                {(['claim', 'payment'] as const).map((k) => {
                  const v = (k === 'claim' ? s?.lifecycle_status : s?.current_status) || null;
                  const options = k === 'claim' ? LIFECYCLE : payStatuses.map((x) => x.key);
                  return (
                    <span key={k} className="cdw-statusitem" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="muted cdw-statuslabel" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 108 }}>
                        {t(k === 'claim' ? 'cdw.claimStatus' : 'cdw.paymentStatus')}
                      </span>
                      <span className="cdw-statusbadge" style={{ position: 'relative', display: 'inline-flex' }}>
                        <span className={`badge ${v ? statusColor(v) : 'gray'}`}
                          style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', padding: '4px 12px', opacity: v ? 1 : 0.7, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          {v ?? '—'} <span style={{ fontSize: 'var(--fs-2xs)', opacity: 0.75 }}>▾</span>
                        </span>
                        {/* Select invisible ENCIMA del badge: click = dropdown nativo. */}
                        <select
                          value=""
                          onChange={(e) => { const nv = e.target.value; if (nv && nv !== v) void changeStatusBadge(k, nv); }}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
                          title={t(k === 'claim' ? 'cdw.changeClaimStatus' : 'cdw.changePaymentStatus')}
                        >
                          <option value="">{v ?? '—'}</option>
                          {options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </span>
                    </span>
                  );
                })}
              </span>
              {s?.preferred_language && <span className="badge cdw-langbadge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--info-soft)', color: 'var(--info)', fontWeight: 'var(--fw-bold)' }}><Globe size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {etiquetaIdioma(s.preferred_language)}</span>}
              {s?.archived_at && <span className="badge gray cdw-archbadge" style={{ fontSize: 'var(--fs-2xs)' }}><Archive size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('archive.archived')}</span>}
            </div>
            <div className="cdw-addr" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              {/* El pin solo se ve en móvil (mockup .m-caddr); en desktop queda display:none. */}
              {/* Videos del inspector: chip al lado de la dirección; click → el cuadro Videos del tab Chat. */}
              <ClaimVideosChip claimId={claimId} onGo={() => { setTab('chat'); setTimeout(() => document.getElementById('sec-videos')?.scrollIntoView({ block: 'center' }), 250); }} />
              {s?.address && <><MapPin size={12} strokeWidth={1.75} className="cdw-addr-pin" /><span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>{s.address}</span><CopyBtn value={s.address} title={t('set.address')} /></>}
            </div>
            {/* File a new claim: se nota también desde las colas, sin abrir el claim entero. */}
            {(s?.previous_claim || s?.next_claim) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {s.previous_claim && (
                  <Link to={`/claims/${claimId}?tab=previous-claim`} className="fnc-chip" title={t('fnc.prevHint', { n: s.previous_claim.claim_number || `#${s.previous_claim.id}` })}>
                    <FolderClock size={14} strokeWidth={1.75} /> <span>{t('fnc.prevAvailable')}</span>
                    <span className="fnc-chip-num">{s.previous_claim.claim_number || `#${s.previous_claim.id}`}</span> <span aria-hidden>↗</span>
                  </Link>
                )}
                {s.next_claim && (
                  <Link to={`/claims/${s.next_claim.id}`} className="fnc-chip next" title={t('fnc.nextHint')}>
                    <FilePlus2 size={14} strokeWidth={1.75} /> <span>{t('fnc.nextFiled')}</span>
                    <span className="fnc-chip-num">{s.next_claim.claim_number || `#${s.next_claim.id}`}</span> <span aria-hidden>↗</span>
                  </Link>
                )}
              </div>
            )}
            {/* Aseguradora MÁS GRANDE (el claim# ahora vive arriba, en el chip NEW/EXISTING)
                + el CONTRATISTA al lado (pedido del dueño: saber quién es sin abrir el item card). */}
            <div className="cdw-chiprow" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
              {/* Con etiqueta (Paula 2026-09-23): "Insurance: Allstate", no un nombre suelto. */}
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-body)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cdw.insurance')}</span>
              {s?.insurance_company ? <InsuranceLink name={s.insurance_company} /> : <span className="muted">—</span>}
              {/* LA FECHA DE LA INSPECCIÓN en el encabezado: la próxima, o la última si ya pasó. Click → Scheduling. */}
              <span className="muted" style={{ fontWeight: 'var(--fw-body)' }}>·</span>
              <button type="button" className="btn ghost sm" onClick={() => setTab('scheduling')} title={t('cdw.tabScheduling')}
                style={{ fontSize: 'var(--fs-xs)', display: 'inline-flex', alignItems: 'center', gap: 5, color: s?.next_inspection ? (s.next_inspection.done ? 'var(--green)' : s.next_inspection.upcoming ? 'var(--accent)' : 'var(--orange)') : 'var(--muted)' }}>
                <CalendarDays size={13} strokeWidth={1.75} />
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cdw.inspection')}</span>
                {s?.next_inspection ? (
                  <span style={{ fontWeight: 'var(--fw-semibold)' }}>
                    {new Date(s.next_inspection.scheduled_start).toLocaleString(localeUI(), { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {s.next_inspection.who ? ` · ${s.next_inspection.who}` : ''}{s.next_inspection.done ? ` · ${t('cdw.inspectionDone')}` : ''}
                  </span>
                ) : <span className="muted">{t('cdw.noInspection')}</span>}
              </button>
              {s?.contractor_name && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-sm)' }}>
                  <span className="muted" style={{ fontWeight: 'var(--fw-body)' }}>·</span>
                  <Hammer size={14} strokeWidth={1.75} /> <ContractorLink name={s.contractor_name} contractorId={s.contractor_id} />
                </span>
              )}
            </div>
          </div>
          <div className="cdw-actions" style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {s?.companycam_url && (
              <a href={s.companycam_url} target="_blank" rel="noopener noreferrer" className="btn ghost sm" style={{ textDecoration: 'none' }}><Camera size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdw.viewPhotos')}</a>
            )}
            {/* ATR / Informative videos al lado de View photos (pedido del dueño 2026-09-30): el mismo componente de la tarjeta del claim. */}
            <ClaimVideoButtons claimId={claimId} autoOpen={false} />
            <button className="btn sm" onClick={() => nav(`/claims/${claimId}`)}><Maximize2 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdw.openClaim')}</button>
            <button className="btn ghost sm" onClick={onClose} title="Close"><X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
          </div>
        </div>

      </div>{/* /CABEZAL FIJO (nombre + dirección + botones) */}

      {/* SCROLL ÚNICO (pedido): las acciones + los views + los URGENT ALERTS scrollean y SE VAN
          hacia arriba; la barra de PESTAÑAS es STICKY (sube y se pega justo debajo de la dirección);
          el contenido de la pestaña (chat) llena TODO el alto de abajo. */}
      <div
        className="cdw-scroll"
        style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
        onScroll={(e) => setCompact(e.currentTarget.scrollTop > 56)}
      >
        {/* CABEZAL MÓVIL (≤900px por CSS; en desktop no existe): vive DENTRO del scroll — la
            única parte fija del drawer en el teléfono es el appbar ‹ Claims. Con datos reales el
            cabezal de desktop reorganizado por CSS crecía más alto que la pantalla dentro de la
            zona fija y el claim quedaba "pegado" (reporte del dueño 2026-08-08 con foto). */}
        <div className="cdw-mhead">
          <h2 className="cdw-mhead-name">{s?.homeowner_name || (loading ? '…' : t('claims.statusNew'))}</h2>
          <div className="cdw-mhead-chips">
            {/* Botón de FOTOS junto al nombre (pedido del dueño: "un botón bonito que diga fotos"). */}
            {s?.companycam_url && (
              <a className="cdw-mhead-photos" href={s.companycam_url} target="_blank" rel="noopener noreferrer">
                <Camera size={13} strokeWidth={1.9} /> {t('cdw.viewPhotos')}
              </a>
            )}
            <ClaimVideoButtons claimId={claimId} autoOpen={false} />
            {s?.preferred_language && <span className="badge" style={{ background: 'var(--info-soft)', color: 'var(--info)', fontWeight: 'var(--fw-bold)' }}><Globe size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {etiquetaIdioma(s.preferred_language)}</span>}
            {s?.insurance_company && <InsuranceLink name={s.insurance_company} />}
            {s?.contractor_name && <span className="cdw-mhead-co"><Hammer size={12} strokeWidth={1.75} /> <ContractorLink name={s.contractor_name} contractorId={s.contractor_id} /></span>}
            {s?.archived_at && <span className="badge gray"><Archive size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('archive.archived')}</span>}
          </div>
          {s?.address && (
            <div className="cdw-mhead-addr"><MapPin size={13} strokeWidth={1.75} /> <span>{s.address}</span></div>
          )}
          <div className="cdw-mhead-statuses">
            {(['claim', 'payment'] as const).map((k) => {
              const v = (k === 'claim' ? s?.lifecycle_status : s?.current_status) || null;
              const options = k === 'claim' ? LIFECYCLE : payStatuses.map((x) => x.key);
              return (
                <span key={k} className="cdw-mhead-status" style={{ position: 'relative' }}>
                  <span className={`badge ${v ? statusColor(v) : 'gray'}`} style={{ width: '100%', justifyContent: 'space-between', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 11px', fontWeight: 'var(--fw-bold)' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v ?? (k === 'claim' ? t('cdw.claimStatus') : t('cdw.paymentStatus'))}</span>
                    <span style={{ opacity: 0.75, flexShrink: 0 }}>▾</span>
                  </span>
                  <select
                    value=""
                    onChange={(e) => { const nv = e.target.value; if (nv && nv !== v) void changeStatusBadge(k, nv); }}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
                    title={t(k === 'claim' ? 'cdw.changeClaimStatus' : 'cdw.changePaymentStatus')}
                  >
                    <option value="">{v ?? '—'}</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </span>
              );
            })}
          </div>
        </div>
        <div style={{ padding: '12px 18px 0' }}>
        {/* BARRA DE DECISIÓN (rediseño TP): mismas acciones y endpoints de siempre; el color
            sólido fuerte queda RESERVADO a los botones que mueven el claim de etapa
            (Approved / Approved for repair / Denied). Hold, Cancel y Archive quedan neutros. */}
        <div className="cdw-decision" style={{ border: '1px solid var(--line)', borderRadius: 12, background: 'var(--surface-2)', overflow: 'hidden' }}>
          <DecisionBar label={t('cwa.decision')}>
            {decisionButtons}
            <span style={{ flex: 1 }} />
            <span onClick={(e) => e.stopPropagation()}><AddToViewSelect claimId={claimId} onChanged={reloadDepts} /></span>
          </DecisionBar>
        </div>

        {/* MÓVIL: dos botones a lo ancho en vez de la tira scrolleable (pedido del dueño con
            foto): Decision y Add to view abren modals CENTRADOS, no a pantalla completa. */}
        <div className="cdw-mactions">
          <button type="button" className="btn ghost sm" onClick={() => setMDecide(true)}>
            <Check size={14} strokeWidth={1.75} /> {t('cwa.decision')}
          </button>
          <button type="button" className="btn ghost sm" onClick={() => setMViewPick(true)}>
            <Plus size={14} strokeWidth={1.75} /> {t('copyq.addToView')}
          </button>
        </div>
        {mDecide && (
          <div className="cdw-mmodal-backdrop" onClick={() => setMDecide(false)}>
            <div className="cdw-mmodal" onClick={(e) => e.stopPropagation()}>
              <div className="cdw-mmodal-head">
                <b>{t('cwa.decision')}</b>
                <button type="button" className="btn ghost sm" onClick={() => setMDecide(false)}><X size={15} strokeWidth={1.75} /></button>
              </div>
              {/* Los MISMOS botones/handlers de la barra; cualquier click cierra el modal. */}
              <div className="cdw-mmodal-body" onClickCapture={() => setMDecide(false)}>
                {decisionButtons}
              </div>
            </div>
          </div>
        )}
        {mViewPick && (
          <div className="cdw-mmodal-backdrop" onClick={() => setMViewPick(false)}>
            <div className="cdw-mmodal" onClick={(e) => e.stopPropagation()}>
              <div className="cdw-mmodal-head">
                <b>{t('copyq.addToView')}</b>
                <button type="button" className="btn ghost sm" onClick={() => setMViewPick(false)}><X size={15} strokeWidth={1.75} /></button>
              </div>
              <div className="cdw-mmodal-body">
                <AddToViewSelect claimId={claimId} onChanged={() => { reloadDepts(); setMViewPick(false); }} />
              </div>
            </div>
          </div>
        )}

        {/* DÓNDE ESTÁ EL CLAIM, ordenado siempre igual: Claims · Payments · Supplements.
            Arriba la VISTA (el status) y debajo el EQUIPO que la tiene. Los tres salen SIEMPRE,
            aunque estén vacíos: antes era una tira de chips en el orden que viniera, y no se
            podía leer de un vistazo si a un depto le faltaba equipo o si el claim ni estaba ahí.
            Si el claim está además en otro depto (intake/scheduling), se agrega al final. */}
        <div className="cdw-views">
          {(() => {
            const FIXED = ['claims', 'payments', 'supplements'];
            const byKey = new Map(depts.map((d) => [String(d.department_key ?? d.department_name).toLowerCase(), d]));
            const extras = depts.filter((d) => !FIXED.includes(String(d.department_key ?? '').toLowerCase()));
            const cells: Array<{ key: string; label: string; d: DeptPresence | undefined }> = [
              ...FIXED.map((k) => ({ key: k, label: t(`dept.${k}`), d: byKey.get(k) })),
              ...extras.map((d) => ({ key: String(d.department_key ?? d.department_name), label: d.department_name, d })),
            ];
            return cells.map(({ key, label, d }) => {
              const fu = d?.next_follow_up_at ? followUpState(d.next_follow_up_at) : null;
              const fuc = fu ? FOLLOWUP_COLOR[fu] : null;
              const canGo = Boolean(d?.department_key && d?.status);
              // TODO lo del depto en su cuadro (feedback 2026-07-29): Payments puede estar a
              // la vez en una cola por PAYMENT STATUS (claims.current_status) y en una working
              // view (claim_departments) — antes solo se veía la segunda y parecía perdido.
              const payStatus = key === 'payments' ? (s?.current_status || null) : null;
              const showPay = Boolean(payStatus && payStatus !== d?.status);
              // Dept card estilo mockup: hairline de 1px + radio 12 (la clase trae 1.5px/10).
              return (
                <div key={key} className={`cdw-view${d ? '' : ' cdw-view-empty'}`} style={{ borderWidth: 1, borderRadius: 12 }}>
                  <div className="cdw-view-dept">{label}</div>
                  {/* La(s) VISTA(s): clic lleva a esa cola. */}
                  {d?.status ? (
                    <button
                      className="cdw-view-status"
                      onClick={() => { if (canGo) nav(`/dept/${d!.department_key}/queue/${encodeURIComponent(d!.status!)}`, { state: { back: true } }); }}
                      title={t('cdw.goToView')}
                      style={{ cursor: canGo ? 'pointer' : 'default' }}
                    >
                      <span className={`badge ${statusColor(d.status)}`} style={{ fontSize: 'var(--fs-2xs)' }}>{d.status}</span>
                    </button>
                  ) : !showPay ? (
                    <span className="muted cdw-view-none">{d ? t('cdw.noStatus') : t('cdw.notHere')}</span>
                  ) : null}
                  {showPay && (
                    <span className={`badge ${statusColor(payStatus)}`} style={{ fontSize: 'var(--fs-2xs)' }} title={t('cdw.payStatusChip')}>
                      $ {payStatus}
                    </span>
                  )}
                  {/* El EQUIPO, debajo. Clic = task; ✕ lo quita; + asigna (feedback 2026-07-29). */}
                  {d?.assigned_team_name ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <button
                        className="cdw-view-team"
                        onClick={() => { setTaskPresetTeam(d.assigned_team_name); setTab('tasks'); }}
                        title={t('cdw.assignTaskTo', { team: d.assigned_team_name })}
                      >
                        <Users size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {d.assigned_team_name}
                      </button>
                      <button
                        className="btn ghost sm"
                        style={{ padding: '0 3px', fontSize: 'var(--fs-2xs)', color: 'var(--red)', border: 0, background: 'transparent' }}
                        title={t('cdw.teamRemove', { team: d.assigned_team_name })}
                        disabled={teamEditBusy === key}
                        onClick={() => void removeDeptTeam(key, d.assigned_team_name!)}
                      ><X size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {teamPickFor === key ? (
                        <select
                          className="input sm" style={{ fontSize: 'var(--fs-2xs)', maxWidth: 130 }} autoFocus defaultValue=""
                          disabled={teamEditBusy === key}
                          onChange={(e) => { if (e.target.value) void assignDeptTeam(key, e.target.value); }}
                          onBlur={() => setTeamPickFor(null)}
                        >
                          <option value="">{teamOptions[key] ? t('cdw.teamPick') : t('common.loadingDots')}</option>
                          {(teamOptions[key] ?? []).map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                        </select>
                      ) : (
                        <button
                          className="btn ghost sm"
                          style={{ padding: '0 5px', fontSize: 'var(--fs-2xs)' }}
                          title={t('cdw.teamAdd')}
                          onClick={() => { setTeamPickFor(key); void loadTeamOptions(key); }}
                        ><Users size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> <Plus size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                      )}
                    </span>
                  )}
                  {fuc && d?.next_follow_up_at && (
                    <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', color: fuc.fg }}><AlarmClock size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {followUpLabel(d.next_follow_up_at)}</span>
                  )}
                </div>
              );
            });
          })()}
        </div>

        {/* Historial de asignaciones (AM + teams): botoncito discreto bajo los dept cards; no
            estorba en móvil (icono solo, alineado a la derecha) y abre el modal centrado z4000. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <button type="button" className="btn ghost sm ash-open" title={t('ash.title')} onClick={() => setShowAsgHist(true)}>
            <History size={13} strokeWidth={1.75} />
          </button>
        </div>
        {showAsgHist && <AssignmentHistoryModal claimId={claimId} onClose={() => setShowAsgHist(false)} />}

        {/* ADJUSTER INFO en el header del drawer (pedido de Paula 2026-08-13: "Can I get a view
            here where I can add the adjusters info?") — existía solo dentro del Item card; acá
            queda visible y editable sin importar la pestaña, igual que en el claim completo. */}
        <AdjusterInfoBox claimId={claimId} initial={s?.adjuster_info} compact />

        {/* URGENT ALERTS arriba (pedido): TODAS juntas, no importa el departamento — el endpoint
            ya devuelve todas; se ven como banners rojos + botón "Add urgent alert". */}
        <div style={{ marginTop: 12 }}>
          <ClaimAlerts claimId={claimId} />
        </div>
        </div>{/* /franja de alerts+acciones+chips (se va con el scroll) */}

        {/* PESTAÑAS estilo Monday — STICKY: al hacer scroll suben y se pegan justo debajo de la
            dirección, dándole todo el espacio de abajo al contenido/chat. */}
        <div className="cdw-tabs" style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--line)', overflowX: 'auto',
          position: 'sticky', top: 0, zIndex: 3, background: 'var(--card)', padding: '10px 18px 0' }}>
          {/* Tabs limpios estilo mockup: la activa en acento + subrayado; el resto muted. */}
          {TABS.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', whiteSpace: 'nowrap',
                padding: '8px 12px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)',
                color: tab === tb.key ? 'var(--accent)' : 'var(--muted)',
                borderBottom: tab === tb.key ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1, transition: 'color 0.13s ease',
              }}>
              {tb.label}
            </button>
          ))}
        </div>

        {/* CONTENIDO de la pestaña — fluye DENTRO del scroll único, debajo de las pestañas sticky. */}
        <div style={{ padding: 16 }}>
        {tab === 'chat' && (
          /* En móvil, .clchat-msimple deja SOLO los mensajes (el mockup): el chrome del chat
             (título, buscador, editor grande, selects) se esconde y se escribe con el composer
             fijo de abajo. En desktop el wrapper no hace nada. */
          <div className="clchat-msimple">
            <ClaimChat key={`gc-${chatBump}`} claimId={claimId} channel={null} defaultChannel={s?.owner_dept ?? null} />
            {/* Los videos del inspector (ATR e informativos), también en la ventana del claim (pedido del dueño 2026-09-19). */}
            <div style={{ marginTop: 14 }}><ClaimVideosBox claimId={claimId} /></div>
          </div>
        )}
        {tab === 'item' && <ItemCardTab claimId={claimId} s={s} onSaved={reloadSummary} />}
        {tab === 'tasks' && <TasksTab claimId={claimId} presetTeam={taskPresetTeam} />}
        {tab === 'info-pending' && <InfoPendingBox claimId={claimId} sourceStage="Supplementing" />}
        {/* Files en LÍNEAS a todo el ancho (sin grid de cuadros). */}
        {tab === 'files' && <ClaimFiles claimId={claimId} />}
        {tab === 'intake' && (
          <div style={{ display: 'grid', gap: 12 }}>
            {/* Contract y Welcome call LADO A LADO, mismo ancho. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 12, alignItems: 'start' }}>
              <ContractPanel claimId={claimId} onChanged={reloadSummary} />
              <WelcomeCallBox claimId={claimId} onChanged={() => { reloadSummary(); reloadDepts(); }} />
            </div>
            {/* Appraiser contract (global, lo firma solo el homeowner) debajo del PA contract. */}
            <ContractPanel claimId={claimId} kind="appraiser" onChanged={reloadSummary} />
            <InfoPendingBox claimId={claimId} sourceStage="Waiting Documents" compact />
            {/* ARCHIVOS EN CLAIM REVIEW, A LA VISTA Y SEPARADOS.
                Pedido de Manuel (2026-08-26): "en claim review también quiero ver files, pero los
                del contratista separado, y que no esté desplegado, que se vean a simple vista".
                Esta pestaña no mostraba NINGÚN archivo: para ver lo que el contratista mandó había
                que salir a la pestaña Files y buscarlo entre los 20.959 del claim.
                Los del contratista van PRIMERO y aparte porque son los que se revisan acá — es lo
                que llegó de afuera y todavía nadie miró. Debajo, el resto. Sin plegar ninguno de
                los dos: el que hay que abrir para verlo es el que no se mira. */}
            <ContractorFilesPanel claimId={claimId} />
            <ClaimFiles claimId={claimId} />
            <ClaimChat claimId={claimId} channel="intake" title="Intake Department Updates" />
          </div>
        )}
        {tab === 'scheduling' && (
          <div style={{ display: 'grid', gap: 14 }}>
            {/* ARRIBA DE TODO: a quién llamar del lado del seguro si hay que reagendar (Manuel 2026-09-23). */}
            <InspectionContactBox claimId={claimId} />
            {/* Mitad y mitad: citas a la izquierda, Scheduling Department Update a la derecha. */}
            <div className="email-split has-side">
              <ClaimAppointmentsPanel claimId={claimId} clientName={s?.homeowner_name} />
              <ClaimChat claimId={claimId} channel="scheduling" title="Scheduling Department Updates" />
            </div>
            {/* Timeline de INSPECCIONES (pedido del dueño): la historia completa de las visitas
                con el mismo look del tracker de Supplements/Payments. */}
            <div className="card" style={{ padding: 14 }}>
              <InspectionTimeline claimId={claimId} />
            </div>
            {/* Los videos del inspector (ATR e informativos) de este claim. */}
            <ClaimVideosBox claimId={claimId} />
          </div>
        )}
        {/* CLAIMS: la pestaña que faltaba entre Scheduling y Payments (pedido del equipo 2026-10-08).
            Por ahora solo los updates del departamento; el resto de la sección se agrega a pedido. */}
        {tab === 'claims' && <ClaimChat claimId={claimId} channel="claims" title={t('cd.claimsUpdates')} />}
        {tab === 'payments' && <ClaimPaymentsFull claimId={claimId} paymentHold={s?.payment_hold} holdNote={s?.payment_hold_note} onHoldChanged={reloadSummary} contractorName={s?.contractor_name} />}
        {tab === 'contractor' && (
          /* CONTRACTOR (pedido del dueño): el chat con el contratista — lo que el staff postea
             se copia al CO (shareContractorChatMessage) y sale también en el General chat
             identificado — + TODOS los files que el contractor subió por JobFlow. */
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="card">
              <ClaimChat claimId={claimId} channel="contractor" title={t('cd.tabContractor')} />
            </div>
            <ContractorFilesCard claimId={claimId} />
          </div>
        )}
        {tab === 'historia' && <ClaimHistory claimId={claimId} />}
        {tab === 'supplements' && (
          <div style={{ display: 'grid', gap: 14 }}>
            {/* Primero la revisión: es lo que frena el cierre del claim. */}
            <SupplementReview claimId={claimId} />
            <SupplementsTimeline claimId={claimId} />
            {/* Envíos al carrier: es el arranque del reloj del tramo interno (mig 0375). Va
                arriba del chat porque marcar el envío es la acción, y el chat es el seguimiento. */}
            <SupplementSubmissions claimId={claimId} />
            <ClaimChat claimId={claimId} channel="supplements" title={t('cd.supplementsUpdates')} />
            {/* Los files, acá mismo: desde el bono se entra a esta pestaña para decidir un bypass,
                y esa decisión se toma leyendo las notas Y mirando los estimados. Mandar a la
                persona a otra pestaña para ver el PDF rompía justo ese recorrido. */}
            <ClaimFiles claimId={claimId} />
          </div>
        )}
        {tab === 'ho-comm' && <HoCommunicationTab claimId={claimId} phone={s?.phone ?? null} />}
        {tab === 'ho-updates' && <HoUpdatesTab claimId={claimId} />}
        {tab === 'emails' && <ClaimEmailBox claimId={claimId} departmentKey={s?.owner_dept ?? undefined} />}
        </div>{/* /contenido de la pestaña */}
      </div>{/* /SCROLL ÚNICO */}
      {/* COMPOSER RÁPIDO del mockup (solo móvil, tab Chat): escribir un update y mandarlo sin
          buscar el editor. Posta al General chat; el editor completo sigue en el tab. */}
      {tab === 'chat' && (
        <div className="cdw-composer"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const fs = Array.from(e.dataTransfer.files ?? []); if (fs.length) setQuickFiles((prev) => [...prev, ...fs]); }}>
          {/* Clip del mockup: adjuntar; el badge dice cuántos van con el update. */}
          <label className="cdw-composer-clip" title={t('upd.attach')}>
            <Paperclip size={18} strokeWidth={1.75} />
            {quickFiles.length > 0 && <span className="cdw-composer-nfiles">{quickFiles.length}</span>}
            <input type="file" multiple style={{ display: 'none' }} disabled={quickBusy}
              onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) setQuickFiles((prev) => [...prev, ...fs]); e.target.value = ''; }} />
          </label>
          <input
            className="input"
            value={quick}
            onChange={(e) => setQuick(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void sendQuick(); }}
            placeholder={t('cd.writeUpdate')}
            disabled={quickBusy}
          />
          <button type="button" className="cdw-composer-send" onClick={() => void sendQuick()} disabled={quickBusy || (!quick.trim() && quickFiles.length === 0)} aria-label="Send">
            <SendIcon size={16} strokeWidth={1.9} />
          </button>
        </div>
      )}
    </div>
  );
}
